import React, { createContext, useContext, useState, useCallback } from 'react'
import { zeroAddress } from 'viem'
import { NetworkTypes } from '@provex/eip712-utils'
import { getEvmClientByChainId } from 'sacrifice-utils'
import { getCryptoIconUrl, getCoinInfo } from '../utils/crypto-icons'
import { getTokenImageUrl } from '../utils/token-image-mapping'
import { getNativeTokenSymbol } from '../utils/token-helpers'
import { gibMetadataService } from '../utils/gib-metadata-service'
import * as keys from '@provex/eip712-utils/keys'
import { IMAGE_SERVER_URL } from '../config/app'
import { erc20Read } from 'sacrifice-utils'
import { waitForImageLoading } from '../utils/delayed-image-loading'

export interface TokenMetadata {
  symbol: string
  name: string
  decimals: number
  iconUrl: string
  lastUpdated: number
}

// Utility metadata object with guaranteed defaults
export interface TokenMetadataWithDefaults {
  symbol: string
  name: string
  decimals: number
  iconUrl: string
}

interface TokenMetadataContextType {
  getTokenMetadata: (inputs: keys.TokenIdentifier) => Promise<TokenMetadata>
  getCachedTokenMetadata: (inputs: keys.TokenIdentifier) => TokenMetadata | null
  clear: (inputs: keys.TokenIdentifier[]) => void
  preloadTokenMetadata: (tokens: keys.TokenIdentifier[]) => Promise<void>
  // Utility function that returns guaranteed defaults
  getTokenMetadataWithDefaults: (inputs: keys.TokenIdentifier) => TokenMetadataWithDefaults
  preloadAndRefresh: (tokens: keys.TokenIdentifier[], onComplete?: () => void) => Promise<void>
  // Check if token metadata is currently loading
  isTokenLoading: (inputs: keys.TokenIdentifier) => boolean
}

const TokenMetadataContext = createContext<TokenMetadataContextType | undefined>(undefined)

const CACHE_DURATION = 60 * 60 * 1000 // 1 hour

interface TokenMetadataProviderProps {
  children: React.ReactNode
}

export const TokenMetadataProvider: React.FC<TokenMetadataProviderProps> = ({ children }) => {
  // In-flight request map: concurrent callers for the same token await the SAME
  // promise instead of polling the cache (a poll with no deadline spins forever
  // when the awaited fetch never resolves). Entries are removed in a finally,
  // so the map only ever holds genuinely in-flight requests.
  const [inFlightRequests] = useState<Map<string, Promise<TokenMetadata>>>(() => new Map())
  const [tokenCache, setTokenCache] = useState<Map<string, TokenMetadata>>(() => new Map())

  // Load Gib metadata ONLY when actually needed (on-demand) to avoid blocking critical path
  // This ensures the 1.8s request doesn't block initial page load
  // The metadata will be loaded lazily when getTokenMetadata is first called

  const getCachedTokenMetadata = useCallback(
    (inputs: keys.TokenIdentifier): TokenMetadata | null => {
      const cacheKey = keys.token(inputs)
      const cached = tokenCache.get(cacheKey)

      if (cached && Date.now() - cached.lastUpdated < CACHE_DURATION) {
        return cached
      }

      return null
    },
    [tokenCache]
  )

  const loadEvmTokenMetadata = useCallback(
    async (chainId: number, tokenAddress: string): Promise<Partial<TokenMetadata>> => {
      // console.log('🔍 Loading EVM token metadata for', tokenAddress, chainId)

      if (tokenAddress === zeroAddress) {
        // Native token - no RPC call needed, can return immediately
        const symbol = getNativeTokenSymbol(chainId)
        // Override Monad (chain ID 143) to use local image
        const iconUrl =
          chainId === 143
            ? '/monad_logo.png'
            : chainId === 4326
              ? '/megaeth_logo.svg'
              : `${IMAGE_SERVER_URL}/image/${chainId}`
        return {
          symbol,
          name: symbol,
          decimals: 18,
          iconUrl,
        }
      }

      // Defer ERC20 metadata loading (including RPC calls) until after critical path
      // This prevents blocking the initial page load with expensive RPC requests
      await waitForImageLoading()

      const client = getEvmClientByChainId(chainId)

      // Try Gib.show metadata first for faster lookups
      // Gib metadata service already uses waitForImageLoading() internally
      // Trigger loading if not already started (non-blocking)
      if (!gibMetadataService.isLoaded()) {
        gibMetadataService.loadMetadata().catch(() => {
          // Silently fail - RPC fallback will work
        })
      }

      // Check if metadata is available (might be null if still loading)
      const gibMetadata = gibMetadataService.getTokenMetadata(chainId, tokenAddress)
      if (gibMetadata) {
        console.log(
          `✅ Using Gib.show metadata for ${gibMetadata.symbol} (${chainId}:${tokenAddress})`
        )
        return {
          symbol: gibMetadata.symbol,
          name: gibMetadata.name,
          decimals: gibMetadata.decimals,
          iconUrl: gibMetadata.logoURI,
        }
      }

      // RPC fallback - now deferred until after critical path. Mirror the
      // non-EVM path's graceful degradation: a failed RPC must fall back to an
      // empty-symbol record (icon renders initials) rather than rejecting and
      // crashing every direct caller of getTokenMetadata.
      const lowerToken = tokenAddress.toLowerCase()
      try {
        const result = await erc20Read(client, [lowerToken])
        const metadata = result.get(lowerToken)
        if (metadata) {
          return {
            symbol: metadata.symbol,
            name: metadata.name,
            decimals: metadata.decimals,
            iconUrl: getTokenImageUrl(NetworkTypes.EVM, chainId, lowerToken),
          }
        }
      } catch (error) {
        console.warn(`Failed to load EVM token metadata for ${chainId}:${lowerToken}:`, error)
      }

      return {
        symbol: '',
        name: '',
        decimals: 18,
        iconUrl: '',
      }
    },
    []
  )

  const loadNonEvmTokenMetadata = useCallback(
    async (networkType: NetworkTypes, chainId: number): Promise<Partial<TokenMetadata>> => {
      try {
        const [iconUrl, coinInfo] = await Promise.all([
          getCryptoIconUrl(networkType, chainId),
          getCoinInfo(networkType, chainId),
        ])

        if (coinInfo) {
          // Default decimals for common non-EVM tokens
          let decimals = 18
          if (networkType === NetworkTypes.BIP32) {
            if (chainId === 0) decimals = 8
            if (chainId === 2) decimals = 8
            if (chainId === 3) decimals = 8
            if (chainId === 145) decimals = 8
            if (chainId === 5) decimals = 8
            if (chainId === 133) decimals = 8
          }
          if (networkType === NetworkTypes.MONERO) decimals = 12
          // if (networkType === NetworkTypes.RIPPLE) decimals = 6
          // if (networkType === NetworkTypes.TVM) decimals = 6
          // if (networkType === NetworkTypes.MEMO) decimals = 6
          if (networkType === NetworkTypes.SOLANA) decimals = 9

          return {
            symbol: coinInfo.symbol || '',
            name: coinInfo.name || '',
            decimals,
            iconUrl: iconUrl || '',
          }
        }
      } catch (error) {
        console.warn(`Failed to load non-EVM token metadata for ${networkType}:${chainId}:`, error)
      }

      return {
        symbol: '',
        name: '',
        decimals: 18,
        iconUrl: '',
      }
    },
    []
  )

  const getTokenMetadata = useCallback(
    async (inputs: keys.TokenIdentifier): Promise<TokenMetadata> => {
      const { networkType, chainId, tokenAddress } = inputs
      const cacheKey = keys.token(inputs)

      // Check cache first
      const cached = getCachedTokenMetadata(inputs)
      if (cached) {
        return cached
      }

      // Prevent duplicate requests: a concurrent caller awaits the same promise.
      const inFlight = inFlightRequests.get(cacheKey)
      if (inFlight) {
        return inFlight
      }

      const request = (async (): Promise<TokenMetadata> => {
        let metadata: Partial<TokenMetadata>

        if (networkType === NetworkTypes.EVM) {
          metadata = await loadEvmTokenMetadata(chainId, tokenAddress)
        } else {
          metadata = await loadNonEvmTokenMetadata(networkType, chainId)
        }

        const fullMetadata: TokenMetadata = {
          symbol: metadata.symbol || '',
          name: metadata.name || '',
          decimals: metadata.decimals || 18,
          iconUrl: metadata.iconUrl || '',
          lastUpdated: Date.now(),
        }

        setTokenCache(prevCache => {
          const newCache = new Map(prevCache)
          newCache.set(cacheKey, fullMetadata)
          return newCache
        })
        return fullMetadata
      })()

      inFlightRequests.set(cacheKey, request)

      try {
        return await request
      } finally {
        inFlightRequests.delete(cacheKey)
      }
    },
    [
      getCachedTokenMetadata,
      loadEvmTokenMetadata,
      loadNonEvmTokenMetadata,
      inFlightRequests,
      setTokenCache,
    ]
  )

  const preloadTokenMetadata = useCallback(
    async (tokens: keys.TokenIdentifier[]): Promise<void> => {
      const promises = tokens.map(token =>
        getTokenMetadata(token).catch(error => {
          console.warn(`Failed to preload metadata for ${keys.token(token)}:`, error)
        })
      )

      await Promise.all(promises)
    },
    [getTokenMetadata]
  )

  const clear = useCallback((tokens: keys.TokenIdentifier[]) => {
    setTokenCache(prevCache => {
      const newCache = new Map(prevCache)
      for (const token of tokens) {
        newCache.delete(keys.token(token))
      }
      return newCache
    })
  }, [])

  // Utility function that returns guaranteed defaults
  const getTokenMetadataWithDefaults = useCallback(
    (inputs: keys.TokenIdentifier): TokenMetadataWithDefaults => {
      const metadata = getCachedTokenMetadata(inputs)
      return {
        symbol: metadata?.symbol || '',
        name: metadata?.name || '',
        decimals: metadata?.decimals || 18,
        iconUrl: metadata?.iconUrl || '',
      }
    },
    [getCachedTokenMetadata]
  )

  const preloadAndRefresh = useCallback(
    async (tokens: keys.TokenIdentifier[], onComplete?: () => void): Promise<void> => {
      await preloadTokenMetadata(tokens)
      onComplete?.()
    },
    [preloadTokenMetadata]
  )

  const isTokenLoading = useCallback(
    (inputs: keys.TokenIdentifier): boolean => {
      const cacheKey = keys.token(inputs)
      return inFlightRequests.has(cacheKey)
    },
    [inFlightRequests]
  )

  const contextValue: TokenMetadataContextType = {
    getTokenMetadata,
    getCachedTokenMetadata,
    clear,
    preloadTokenMetadata,
    getTokenMetadataWithDefaults,
    preloadAndRefresh,
    isTokenLoading,
  }

  return (
    <TokenMetadataContext.Provider value={contextValue}>{children}</TokenMetadataContext.Provider>
  )
}

export const useTokenMetadata = (): TokenMetadataContextType => {
  const context = useContext(TokenMetadataContext)
  if (!context) {
    throw new Error('useTokenMetadata must be used within a TokenMetadataProvider')
  }
  return context
}
